// Restore autosaved workflow on mount. useAutoSave skriver till
// localStorage var 30 sek; vid mount läser vi tillbaka senaste snapshot.
// Skipas i view-mode (?mode=view) eftersom user explicit öppnat en
// delad/locked variant. Mount-only — viewOnly/loadState/logAction tas
// medvetet inte som deps eftersom restore bara ska köra en gång.
//
// Lager B: ny key 'workflow-autosave-v2-compact' lagrar compact-format.
// Legacy 'workflow-autosave' (full state) läses fortfarande som
// fallback — efter migration deletas legacy-nyckeln så vi inte växer
// localStorage onödan.

import { useEffect } from 'react';
import type { MultiSheetWorkflowState, WorkflowState } from '@/types/workflow';

interface UseAutosaveRestoreArgs {
  viewOnly: boolean;
  loadState: (state: WorkflowState | MultiSheetWorkflowState) => void;
  logAction: (action: string, detail?: string) => void;
}

export function useAutosaveRestore({ viewOnly, loadState, logAction }: UseAutosaveRestoreArgs) {
  useEffect(() => {
    if (viewOnly) return;
    try {
      let raw = localStorage.getItem('workflow-autosave-v2-compact');
      let isLegacy = false;
      if (!raw) {
        raw = localStorage.getItem('workflow-autosave');
        isLegacy = !!raw;
      }
      if (!raw) return;
      let parsed = JSON.parse(raw);
      // Sanity-check: kräv åtminstone ett blocks-array, sheets-array
      // eller compact `b`-array. Hindrar att korrupt data crashar mount.
      const isMulti = parsed && typeof parsed === 'object' && Array.isArray(parsed.sheets);
      const isSingle = parsed && typeof parsed === 'object' && Array.isArray(parsed.blocks);
      const isCompact = parsed && typeof parsed === 'object' && Array.isArray(parsed.b);
      if (!(isMulti || isSingle || isCompact)) return;

      // Compact-format → expandera till runtime-shape innan loadState.
      if (isCompact) {
        // Lazy-import för att hålla initial bundle nere.
        import('@/lib/compactWorkflow').then(({ expandCompactWorkflow }) => {
          parsed = expandCompactWorkflow(parsed);
          loadState(parsed);
          logAction('Workflow restored', 'från autosave-snapshot (compact)');
        });
        return;
      }
      loadState(parsed);
      logAction('Workflow restored', 'från autosave-snapshot');
      // Migrera legacy → compact: nästa autosave-tick skriver till
      // ny key, så vi kan radera den gamla direkt.
      if (isLegacy) {
        localStorage.removeItem('workflow-autosave');
      }
    } catch {
      // Tyst fail — ingen anledning att blocka mount på korrupt data.
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
